/** @format */
import { Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import { StudyPeriodLocator } from 'common-typescript/types';
import { ParsedStudyPeriodLocator, StudyPeriodLocatorService } from 'sis-components';

@Injectable()
export class ParsedPlannedPeriodService {
    constructor(private readonly _studyPeriodLocatorService: StudyPeriodLocatorService) {}

    /**
     * Returns locators (with parsed data) of all study periods having timings in the given plan.
     */
    getSortedParsedPlannedPeriods(validatablePlan: ValidatablePlan): readonly ParsedPlannedPeriod[] {
        const courseUnitSelectionPeriods: StudyPeriodLocator[] = validatablePlan
            .getAllCourseUnitsInPlan()
            .flatMap(courseUnit => validatablePlan.getPlannedPeriods(courseUnit.id) ?? []);
        const customStudyDraftPeriods: StudyPeriodLocator[] =
            validatablePlan.plan.customStudyDrafts?.flatMap(customStudyDraft => customStudyDraft.plannedPeriods ?? []) ?? [];
        const distinctPlannedPeriodLocators = new Set<StudyPeriodLocator>([...courseUnitSelectionPeriods, ...customStudyDraftPeriods]);

        const result: ParsedPlannedPeriod[] = [];

        for (const locator of distinctPlannedPeriodLocators) {
            const parsedLocator: ParsedStudyPeriodLocator | null = this._studyPeriodLocatorService.parseStudyPeriodLocator(locator);
            if (parsedLocator) {
                result.push({
                    parsedLocator,
                    locator,
                });
            }
        }

        return result.toSorted((a, b) => this.sortByPeriodTime(a.parsedLocator, b.parsedLocator));
    }

    private sortByPeriodTime(a: ParsedStudyPeriodLocator, b: ParsedStudyPeriodLocator): number {
        return this._studyPeriodLocatorService.isStudyPeriodEarlier(a, b)
            ? -1
            : this._studyPeriodLocatorService.isStudyPeriodEarlier(b, a)
              ? 1
              : 0;
    }
}

export interface ParsedPlannedPeriod {
    readonly parsedLocator: ParsedStudyPeriodLocator;
    readonly locator: StudyPeriodLocator;
}
